import { useRef, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { Subject, OperatorFunction, Observable, tap, debounceTime, map } from 'rxjs';

const AUTOTYPER_DETECT_STEP = 25;
const AUTOTYPER_DETECT_LENGTH = 100;
const AUTOTYPER_DETECT_MAX_INTERVAL_MS = 2000;
const DEBOUNCE_TIME = 1000;

export function bufferDebounceTime<T>(time: number = 0): OperatorFunction<T, T[]> {
  return (source: Observable<T>) => {
    let bufferedValues: T[] = [];

    return source.pipe(
      tap(value => bufferedValues.push(value)),
      debounceTime(time),
      map(() => bufferedValues),
      tap(() => (bufferedValues = [])),
    );
  };
}

/**
 * @param onDetect Memorized callback to be called when autotyper is detected
 * @returns updateKeystrokeTimestamp function to be called when a keystroke is typed
 */
const useAutoTyperDetector = (onDetect: () => void) => {
  const keystrokeTimestamp$ = useRef(new Subject<number>());

  const updateKeystrokeTimestamp = useCallback(() => {
    keystrokeTimestamp$.current.next(Date.now());
  }, []);

  useEffect(() => {
    const subscription = keystrokeTimestamp$.current
      .asObservable()
      .pipe(
        tap(() => {
          // console.log('keystroke tap'); // For debug
        }),
        bufferDebounceTime(DEBOUNCE_TIME),
        map(timestamps => {
          const intervals: number[] = [];
          for (let i = 1; i < timestamps.length; i++) {
            intervals.push(timestamps[i] - timestamps[i - 1]);
          }

          const avgKeystrokeIntervalMsPerStep: number[] = [];
          const stdKeystrokeIntervalMsPerStep: number[] = [];

          for (let step = 0; step < intervals.length; step = step + AUTOTYPER_DETECT_STEP) {
            const subset = intervals.slice(step, step + AUTOTYPER_DETECT_LENGTH);
            if (subset.length < 9) {
              continue;
            }

            const avgIntervalMs = _.mean(subset) ?? 0.0;
            const stdIntervalMs = Math.sqrt(_.mean(subset.map(x => (x - avgIntervalMs) ** 2)));

            if (avgIntervalMs > AUTOTYPER_DETECT_MAX_INTERVAL_MS) {
              continue;
            }

            avgKeystrokeIntervalMsPerStep.push(avgIntervalMs);
            stdKeystrokeIntervalMsPerStep.push(stdIntervalMs);
          }
          return { avgKeystrokeIntervalMsPerStep, stdKeystrokeIntervalMsPerStep };
        }),
      )

      .subscribe(({ avgKeystrokeIntervalMsPerStep, stdKeystrokeIntervalMsPerStep }) => {
        if (
          _.mean(avgKeystrokeIntervalMsPerStep) < 5 || // typing way too fast
          _.mean(stdKeystrokeIntervalMsPerStep) < 2 // typing way too steady
        ) {
          onDetect();
          return;
        }
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [onDetect]);

  return { updateKeystrokeTimestamp };
};

export default useAutoTyperDetector;
